import type { Placement } from '~/composables/customForms/pdfMapperFields';

/**
 * Custom Forms — shapes returned by `GET /custom-forms`, `GET /custom-forms/{id}` and
 * `POST /custom-forms/upsert`, plus the JSON contract we keep inside `formData`.
 */

export interface CustomFormFieldSchema {
  $formkit: string;
  name: string;
  label: string;
  id: string;
  help?: string;
  validation?: string;
  /** Tooltip for questions; the HTML content for `blockText` elements */
  helpLong?: string;
  suffixIcon?: string;
  prefixIcon?: string;
  placeholder?: string;
  onSuffixIconClick?: string;
  onPrefixIconClick?: string;
  mask?: string;
  options?: { label: string; value: string }[];
  validationMessages?: Record<string, string>;
  validationVisibility?: string;
}

export interface CustomFormFieldTranslation {
  id?: string;
  languageCode: string;
  translatedName: string;
  translatedTooltip?: string;
}

export interface CustomFormField {
  id: string;
  name: string;
  active: boolean;
  displayOrder: number;
  /** true = Required, false = Asked (optional), null for legacy rows */
  customFormRequired: boolean | null;
  /**
   * "Case Worker Only": left out of the applicant's step (sa-help) and completed by the case worker from
   * the custom form's accordion on the application. Needs the backend to persist and return it (like
   * `customFormRequired`); missing on older rows → false.
   */
  caseWorkerOnly?: boolean | null;
  formkitSchema: CustomFormFieldSchema;
  translations: CustomFormFieldTranslation[];
  /** Ordering lives on the form-step link (updated by PUT /custom-forms/{id}/update-display-order) */
  formFieldFormSteps?: { id: string; displayOrder: number; formStepId?: string; indent?: number }[];
  createdAt: string;
  updatedAt: string;
}

/** Order used on the applicant form: the step link's displayOrder, falling back to the field's own */
export const effectiveDisplayOrder = (f: CustomFormField) => f.formFieldFormSteps?.[0]?.displayOrder ?? f.displayOrder ?? 0;

/**
 * Indent of an element on the applicant's step, like a Tab in Word: 0 (none) to `MAX_INDENT` tab stops of
 * `INDENT_STEP_REM` each. Saved with the display order (`PUT /custom-forms/{id}/update-display-order`) on
 * the step link; sa-help gets it as the step field's `indent`.
 */
export const MAX_INDENT = 10;
export const INDENT_STEP_REM = 2;
export const clampIndent = (level: unknown) => Math.min(Math.max(Math.round(Number(level) || 0), 0), MAX_INDENT);
export const indentOf = (f: CustomFormField) => clampIndent(f.formFieldFormSteps?.[0]?.indent);

export interface CustomFormCondition {
  id?: string;
  conditionText: string;
  isActive?: boolean;
  /**
   * What to do when the applicant skipped the question(s) the condition is based on — see
   * `SKIPPED_ACTIONS` (sa-help `conditions.ts` applies it). Missing → the condition's default.
   */
  hideFormIfAnswerIsMissing?: boolean | null;
  answerSkippedBehavior?: string | null;
}

export interface CustomForm {
  id: string;
  name: string;
  active: boolean;
  displayOrder?: number;
  /** Object since the API switched from a JSON string — see `CustomFormData` */
  formData: CustomFormData | null;
  conditionalRendering: CustomFormCondition[] | null;
  assistanceTypeIds: string[];
  serviceAreaIds: string[];
  basePdfFileId: string | null;
  /** Step of the applicant form holding this form's answers (`application.formkitForm.steps[].stepId`) */
  formStepId?: string;
  /**
   * "Admin Only" (`tbl_form_steps.admin_only`): the form isn't presented to the applicant on the online
   * application — a PDF-only form, or one staff complete from the application. Missing → false.
   */
  adminOnly?: boolean | null;
  /** "Generate PDF" (`tbl_custom_forms.generates_pdf`): false = presentation only, no PDF. Missing → true. */
  generatesPdf?: boolean | null;
  fields: CustomFormField[];
  createdAt: string;
  updatedAt: string;
}

/** Payload of `POST /custom-forms/upsert` */
export interface CustomFormUpsertPayload {
  id: string | null;
  active: boolean;
  /** Blob id of the base (English) PDF template */
  basePdfFileId: string | null;
  name: string;
  displayOrder: number;
  formData: CustomFormData;
  conditionalRendering: {
    conditionText: string;
    hideFormIfAnswerIsMissing?: boolean | null;
    answerSkippedBehavior?: string | null;
  }[];
  assistanceTypesIds: string[];
  serviceAreaIds: string[];
  adminOnly: boolean;
  generatesPdf: boolean;
}

/** Payload of `POST /custom-forms/{formId}/upsert` (a question / HTML block of the form) */
export interface CustomFormFieldUpsertPayload {
  id: string | null;
  active: boolean;
  displayOrder: number;
  formkitSchema: CustomFormFieldSchema;
  customFormRequired: boolean;
  caseWorkerOnly: boolean;
  translations: CustomFormFieldTranslation[];
}

// ---------------------------------------------------------------------------
// formData (PDF generation + field mapping)
// ---------------------------------------------------------------------------

export interface PdfTemplate {
  languageCode: string;
  languageName: string;
  fileName: string;
  /** Blob id returned by POST /files/upload; the PDF is served by GET /files/download/{fileId} */
  fileId?: string;
}

/** Template used for a language: its own, falling back to the base (English) template */
export const templateForLanguage = (formData: CustomFormData, languageCode: string): PdfTemplate | undefined =>
  formData.templates.find((t) => t.languageCode === languageCode && t.fileId) ??
  formData.templates.find((t) => t.languageCode === 'en' && t.fileId);

export interface CustomFormData {
  pdfMode: 'generic' | 'custom';
  /** One uploaded template per language (English is the base template) */
  templates: PdfTemplate[];
  /** Field placements per language template */
  mappings: Record<string, Placement[]>;
}

export const emptyFormData = (): CustomFormData => ({ pdfMode: 'generic', templates: [], mappings: {} });

/**
 * Normalizes `formData` coming from the API: some existing rows have `templates` and/or `mappings`
 * stored as `null` rather than an empty array/object, which crashes any `.filter`/`.forEach` call on
 * them. Use this instead of `formData ?? emptyFormData()` wherever those are read.
 */
export const normalizeFormData = (formData: CustomFormData | null | undefined): CustomFormData => {
  if (!formData) return emptyFormData();
  return {
    pdfMode: formData.pdfMode === 'custom' ? 'custom' : 'generic',
    templates: formData.templates ?? [],
    mappings: formData.mappings ?? {},
  };
};

/** Re-builds the upsert payload of an existing form (used when only `formData` changes, e.g. the PDF mapper). */
export const toUpsertPayload = (form: CustomForm, formData: CustomFormData): CustomFormUpsertPayload => ({
  id: form.id,
  active: form.active,
  basePdfFileId: form.basePdfFileId ?? null,
  name: form.name,
  displayOrder: form.displayOrder ?? 0,
  formData,
  conditionalRendering: (form.conditionalRendering ?? []).map((c) => ({
    conditionText: c.conditionText,
    // kept exactly as stored, so saving anything else never changes the "skipped question" choice
    hideFormIfAnswerIsMissing: !!c.hideFormIfAnswerIsMissing,
    answerSkippedBehavior: c.answerSkippedBehavior ?? null,
  })),
  assistanceTypesIds: form.assistanceTypeIds ?? [],
  serviceAreaIds: form.serviceAreaIds ?? [],
  // always sent as they are, so saving anything else never resets them
  adminOnly: isAdminOnlyForm(form),
  generatesPdf: formGeneratesPdf(form),
});

export const isAdminOnlyForm = (form?: Pick<CustomForm, 'adminOnly'> | null) => !!form?.adminOnly;
export const formGeneratesPdf = (form?: Pick<CustomForm, 'generatesPdf'> | null) => form?.generatesPdf !== false;

/**
 * Custom form "Type" (create/edit form), stored as the two flags:
 * - Presentation to Applicant Only: adminOnly = false, generatesPdf = false
 * - Generation of PDF Only:         adminOnly = true,  generatesPdf = true
 * - Both:                           adminOnly = false, generatesPdf = true
 */
export type CustomFormType = 'presentation' | 'pdf' | 'both';
export const CUSTOM_FORM_TYPE_OPTIONS: { label: string; value: CustomFormType }[] = [
  { label: 'Presentation to Applicant Only', value: 'presentation' },
  { label: 'Generation of PDF Only', value: 'pdf' },
  { label: 'Both', value: 'both' },
];
export const customFormTypeOf = (form?: Pick<CustomForm, 'adminOnly' | 'generatesPdf'> | null): CustomFormType => {
  // admin only means the applicant never sees it: whatever the PDF flag says, all it can be is a PDF form
  if (isAdminOnlyForm(form)) return 'pdf';
  return formGeneratesPdf(form) ? 'both' : 'presentation';
};
export const flagsForCustomFormType = (type: CustomFormType): { adminOnly: boolean; generatesPdf: boolean } => {
  switch (type) {
    case 'presentation':
      return { adminOnly: false, generatesPdf: false };
    case 'pdf':
      return { adminOnly: true, generatesPdf: true };
    default:
      return { adminOnly: false, generatesPdf: true };
  }
};

// ---------------------------------------------------------------------------
// Additional conditions (conditionalRendering)
// ---------------------------------------------------------------------------

export const CONDITION_OPTIONS: { value: string; label: string }[] = [
  { value: 'isTotalIncomeZero', label: 'Total income (sum of all income fields) is $0' },
  { value: 'isHomeless', label: 'Applicant is homeless' },
  { value: 'isVictimOfDomesticViolence', label: 'Applicant is a victim of domestic violence' },
  { value: 'allApplicants', label: 'All applicants' },
];

export const conditionLabel = (conditionText: string) =>
  CONDITION_OPTIONS.find((c) => c.value === conditionText)?.label ?? conditionText;

/**
 * "In the case when the applicant skips the question(s)…" choices of a Presentation Condition, per
 * condition (values shared with sa-help's `conditions.ts`, which applies them). Conditions not listed
 * here (All applicants) aren't based on an answer.
 */
export const SKIPPED_ACTION_OPTIONS: Record<string, { label: string; value: string }[]> = {
  isHomeless: [
    { label: 'Still utilize (present and/or generate a PDF) the form', value: 'utilize' },
    { label: 'Suppress the form presentation and/or PDF generation', value: 'suppress' },
  ],
  isVictimOfDomesticViolence: [
    { label: 'Still utilize (present and/or generate a PDF) the form', value: 'utilize' },
    { label: 'Suppress the form presentation and/or PDF generation', value: 'suppress' },
  ],
  isTotalIncomeZero: [
    { label: 'Treat the skipped question(s) as zero and still total with the other answers', value: 'treatAsZero' },
    {
      label: 'Ignore the skipped question(s) entirely and require at least one of the questions to have a zero',
      value: 'ignoreSkipped',
    },
    { label: 'Suppress the form presentation and/or PDF generation', value: 'suppress' },
  ],
};
export const skippedActionLabel = (conditionText: string, skippedAction?: string | null) =>
  SKIPPED_ACTION_OPTIONS[conditionText]?.find((o) => o.value === skippedAction)?.label ?? '';

// ---------------------------------------------------------------------------
// Question / HTML block types
// ---------------------------------------------------------------------------

/** `$formkit` values of display-only elements (nothing collected from the applicant) */
export const BLOCK_TEXT_TYPE = 'blockText';
export const EMPTY_LINE_TYPE = 'emptyLine';
/** An Empty Line has no admin-given name: it's always listed as this */
export const EMPTY_LINE_LABEL = 'Blank Line';
export const isDisplayOnlyField = (schema?: CustomFormFieldSchema | null) =>
  schema?.$formkit === BLOCK_TEXT_TYPE || schema?.$formkit === EMPTY_LINE_TYPE;

/**
 * Electronic signature question. The applicant draws it in sa-help (`<Signature>` component /
 * FormKit `signature` input); the drawn image is uploaded to blob storage right away (same
 * `/applications/{id}/file` endpoint as every other file answer) and the answer is stored as
 * `{ blobId, userIp, timestamp }` — never the base64 image itself. On PDFs / application views the
 * signature is resolved back to a picture from `blobId` (see `normalizeSignature` / `resolveSignatureImages`
 * in `customFormSummary.ts`).
 */
export const SIGNATURE_TYPE = 'signature';
export const isSignatureField = (schema?: CustomFormFieldSchema | null) => schema?.$formkit === SIGNATURE_TYPE;

/** Answer of a `signature` question as saved in the application step data */
export interface SignatureAnswer {
  /** Id of the image in blob storage (`applications/{id}/file/{blobId}`) — the persisted reference */
  blobId?: string;
  /** PNG data URL (`data:image/png;base64,...`) — legacy shape, from before signatures were uploaded */
  image?: string;
  userIp: string;
  /** ISO 8601 */
  timestamp: string;
  /** Browser name (Chrome, Safari, …) captured from the applicant's device at signing time; absent on older signatures */
  browser?: string;
  /** Device/platform family (iPhone, Android, Windows, …) captured the same way; absent on older signatures */
  device?: string;
}

/**
 * Single check box question: the applicant ticks it or not. Saved as `CHECKBOX_ON_VALUE` when ticked
 * and '' when not (FormKit `on-value` / `off-value`, set where the question is rendered: the schema DTO
 * can't carry them), so "Required" means it has to be ticked.
 */
export const CHECKBOX_TYPE = 'checkbox';
export const CHECKBOX_ON_VALUE = 'Yes';
export const isCheckboxField = (schema?: CustomFormFieldSchema | null) =>
  schema?.$formkit === CHECKBOX_TYPE && !schema?.options?.length;
/** A check box answer that is ticked (`true` / 'true' kept for answers saved without the on-value) */
export const isCheckedAnswer = (raw: unknown) =>
  raw === true || ['yes', 'true', '1', 'on'].includes(`${raw ?? ''}`.trim().toLowerCase());

export const fieldTypeLabel = (schema?: CustomFormFieldSchema | null) => {
  switch (schema?.$formkit) {
    case BLOCK_TEXT_TYPE:
      return 'Block Text';
    case EMPTY_LINE_TYPE:
      return 'Empty Line';
    case 'text':
      return 'Short Text';
    case 'textarea':
      return 'Long Text';
    case 'mask':
      return 'Currency';
    case 'number':
      return 'Numeric';
    case 'date':
      return 'Date';
    case 'select':
      return 'Select';
    case 'file':
      return 'File';
    case SIGNATURE_TYPE:
      return 'Applicant Signature';
    case CHECKBOX_TYPE:
      return 'Check Box';
    default:
      return schema?.$formkit ?? '';
  }
};

/** Options for the "Question Type" filter, in the same order/labels as `fieldTypeLabel` */
export const QUESTION_TYPE_OPTIONS = [
  { value: 'text', label: 'Short Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'mask', label: 'Currency' },
  { value: 'number', label: 'Numeric' },
  { value: 'date', label: 'Date' },
  { value: 'select', label: 'Select' },
  { value: 'file', label: 'File' },
  { value: CHECKBOX_TYPE, label: 'Check Box' },
  { value: SIGNATURE_TYPE, label: 'Signature' },
  { value: BLOCK_TEXT_TYPE, label: 'Block Text' },
  { value: EMPTY_LINE_TYPE, label: 'Empty Line' },
];

// ---------------------------------------------------------------------------
// Client-side report over `form.fields` (no list endpoint per form yet)
// ---------------------------------------------------------------------------

type FilterMap = { [key: string]: { value: any; operator: string }[] };

export const queryCustomFormFields = (
  fields: CustomFormField[],
  filter: FilterMap,
  pagination: { currentPage: number; perPage: number },
  sort: { id: string; desc: boolean }
) => {
  const name = `${filter?.name?.[0]?.value ?? ''}`.trim().toLowerCase();
  const active = `${filter?.active?.[0]?.value ?? ''}`;
  const fieldTypes = Array.isArray(filter?.formkitSchema?.[0]?.value) ? (filter.formkitSchema[0].value as string[]) : [];

  let rows = [...(fields ?? [])];
  if (name) rows = rows.filter((f) => (f.formkitSchema?.label ?? f.name ?? '').toLowerCase().includes(name));
  if (active === 'true') rows = rows.filter((f) => f.active);
  if (active === 'false') rows = rows.filter((f) => !f.active);
  if (fieldTypes.length) rows = rows.filter((f) => fieldTypes.includes(f.formkitSchema?.$formkit ?? ''));

  const key = sort?.id || 'displayOrder';
  const dir = sort?.desc ? -1 : 1;
  const valueOf = (f: CustomFormField): string | number => {
    if (key === 'name') return (f.formkitSchema?.label ?? f.name ?? '').toLowerCase();
    if (key === 'active') return f.active ? 1 : 0;
    if (key === 'formkitSchema') return fieldTypeLabel(f.formkitSchema).toLowerCase();
    if (key === 'createdAt' || key === 'updatedAt') return f[key] ?? '';
    return effectiveDisplayOrder(f);
  };
  rows.sort((a, b) => {
    const va = valueOf(a);
    const vb = valueOf(b);
    if (va < vb) return -1 * dir;
    if (va > vb) return 1 * dir;
    return 0;
  });

  const count = rows.length;
  const start = Math.max(0, (pagination.currentPage - 1) * pagination.perPage);
  const result = rows.slice(start, start + pagination.perPage);
  return {
    success: true,
    data: { result, count, pages: Math.ceil(count / pagination.perPage) },
  };
};

/** How many characters of a Block Text's content it's listed under (it isn't named by the admin) */
export const BLOCK_TEXT_LABEL_LENGTH = 10;

/** Stored Block Text content (`helpLong`: escaped HTML with `<br>` line breaks) -> plain text */
export const blockTextContent = (html?: string | null): string =>
  (html ?? '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');

/** First `BLOCK_TEXT_LABEL_LENGTH` characters of a Block Text, on one line — saved as its label */
export const blockTextLabel = (text?: string | null): string =>
  (text ?? '').replace(/\s+/g, ' ').trim().slice(0, BLOCK_TEXT_LABEL_LENGTH).trim();

/**
 * Name a form element is listed under. Display-only elements aren't named by the admin: an Empty Line is
 * always "Blank Line" and a Block Text is the start of its text ("…" when there's more), whatever older
 * data saved as their name.
 */
export const formElementLabel = (field?: { name?: string | null; formkitSchema?: CustomFormFieldSchema | null } | null) => {
  const schema = field?.formkitSchema;
  if (schema?.$formkit === EMPTY_LINE_TYPE) return EMPTY_LINE_LABEL;
  if (schema?.$formkit === BLOCK_TEXT_TYPE) {
    const text = blockTextContent(schema.helpLong).replace(/\s+/g, ' ').trim();
    const label = blockTextLabel(text);
    if (!label) return schema.label || 'Block Text';
    return text.length > label.length ? `${label}…` : label;
  }
  return schema?.label ?? field?.name ?? '';
};

/** The form's element behind a step field (matched by field id, else by name) */
export const customFormFieldFor = (
  form: CustomForm | null | undefined,
  stepField: { fieldId?: string | null; name?: string | null }
): CustomFormField | undefined =>
  (form?.fields ?? []).find(
    (f) =>
      (!!stepField.fieldId && f.id === stepField.fieldId) ||
      (!!stepField.name && (f.formkitSchema?.name === stepField.name || f.name === stepField.name))
  );

export const isCaseWorkerOnlyField = (
  form: CustomForm | null | undefined,
  stepField: { fieldId?: string | null; name?: string | null }
): boolean => !!customFormFieldFor(form, stepField)?.caseWorkerOnly;
